import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

import FilterSearch from 'components/FilterSearch';
import FilterDropdown from 'components/FilterDropdown';

import { GET, httpOk } from 'helpers/api';
import { DOWNLOAD_FILE, formatDate, formatMoney } from 'helpers/helpers';

import { ReactComponent as WarehouseIcon } from 'assets/icons/sidebar/warehouse.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import WarehouseReplenishment from './modals/RequestRaw';

function Production() {
    const { t } = useTranslation()

    const [dropdown, setDropdown] = useState(false)
    const [warehouseReplenishmentModal, setWarehouseReplenishmentModal] = useState({ show: false })

    const initFilterData = {
        page: 1,
        page_size: 20,
        search: '',
        status: '',
        stock_type: 'production',
    }

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({})

    async function exportFile() {
        await DOWNLOAD_FILE('/recipes/export/', filterData, t('bom'))
    }

    const getData = useCallback(async () => {
        const [response, responseStats] = await Promise.all([
            GET('/recipes/dashboard/', filterData),
            GET('/recipes/', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, ...responseStats.data, });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);


    return (
        <>
            <div>
                <div className="main-content-title">
                    {t('bom')}
                </div>

                <div className="main-content-subtitle">
                    {t('product_composition_management')}
                </div>
            </div>


            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_recipes')}
                            </div>
                            <div className="value">
                                {formatMoney(data?.total_recipes)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('active_products')}
                            </div>
                            <div className="value text-success">
                                {formatMoney(data?.active_products)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('average_composition')}
                            </div>
                            <div className="value">
                                <div className="d-flex align-items-center gap-8">
                                    {data?.average_ingredients}
                                    <div className="fz12 text-second">
                                        {t('ingredients')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('last_update')}
                            </div>
                            <div className="value">
                                {formatDate(data?.last_update)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="d-flex gap-16 w-100">
                    <FilterSearch
                        value={filterData.search}
                        onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
                        placeholder={t('search_by_sku_or_name')}
                    />

                    <FilterDropdown dropdown={dropdown} setDropdown={setDropdown}>
                    </FilterDropdown>
                    <button className="white third" onClick={() => exportFile()}>
                        <DownloadIcon />
                        {t('export')}
                    </button>
                </div>
            </div>

            {data?.results?.map((item, index) => (
                <div className="card" key={index}>

                    <div className="d-flex flex-column gap-8 mb-30">
                        <div className="d-flex align-items-center gap-8">
                            <div className="fz16 text-third">
                                {item.product_name}
                            </div>
                            <div className="alert alert-dark">
                                <WarehouseIcon />
                                {item.type_label}
                            </div>
                        </div>

                        <div className="text-fifth">
                            {t('sku')}: {item.sku} • {t('version')}: {item.current_version} • {t('updated')}: {formatDate(item.version_created_at)}
                        </div>

                        <div className="d-flex align-items-center gap-8">
                            <div className="text-second">
                                {item.type === 'tyle' ?
                                    <>
                                        {item.rows_per_box ? `${item.rows_per_box} ${t('number_of_rows')}` : ''}
                                        {(item.rows_per_box && item.pieces_per_row) && '×'}
                                        {item.pieces_per_row ? `${item.pieces_per_row} ${t('unit_short')}` : ''}
                                        = {item.rows_per_box * item.pieces_per_row} {t('pcs_in_box')}
                                    </>
                                    :
                                    <>
                                        ≈{item.approx_pieces_per_box} {t('pcs_in_box')}
                                    </>
                                }
                            </div>
                            <div className="alert alert-white">
                                {t('calculation')}: {item.calc_basis_label}
                            </div>
                        </div>
                    </div>

                    <div className="text-second mb-16">
                        {t('technologist')}: {item.technologist_name}
                    </div>

                    <div className="text-second mb-12">
                        {t('composition')} (на 1 {item.product_unit_short_name}):
                    </div>

                    {item?.ingredients?.length > 0 &&
                        <table className="table">
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>{t('raw_material')}</th>
                                    <th style={{ width: '20%' }}>{t('quantity')}</th>
                                    <th style={{ width: '20%' }}>{t('unit_short')}</th>
                                    <th style={{ width: '20%' }}>{t('balance')}</th>
                                    <th style={{ width: '20%' }}>{t('actions')}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {item.ingredients?.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.raw_material_name}</td>
                                        <td>{(item.quantity)}</td>
                                        <td>{item.unit_short_name}</td>
                                        <td className={item.is_deficit ? "text-danger" : ""}>
                                            {formatMoney(item.current_stock, 2)} {item.unit_short_name}
                                        </td>
                                        <td>
                                            <button className="white small" onClick={() => setWarehouseReplenishmentModal({ show: true, item })}>
                                                {t('raw_material_receipt')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    }

                    <div className="card-info">
                        <div className="text-second">
                            {t('total_consumption_per_100kg')}:
                        </div>
                        {formatMoney(item.total_consumption_per_unit)} {t('kg_of_raw_material')}
                    </div>

                </div>
            ))}

            <WarehouseReplenishment modal={warehouseReplenishmentModal.show} setModal={() => setWarehouseReplenishmentModal({ show: false })}
                getData={getData} item={warehouseReplenishmentModal.item} />
        </>
    )
}

export default Production